import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, FalhaApi, type ClienteApi, type VistoriaResumoApi } from "../api";
import { Layout } from "../componentes/Layout";
import { PillStatusVistoria } from "../componentes/Pill";
import { useSessao } from "../estado/sessao";

export function ClienteDetalhe() {
  const { usuario } = useSessao();
  const admin = usuario?.papel === "ADMIN";
  const { id } = useParams<{ id: string }>();
  const [clientes, setClientes] = useState<ClienteApi[] | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useState<"responsaveis" | "vistorias">("responsaveis");

  function carregar() {
    api
      .listarClientes()
      .then(({ clientes }) => setClientes(clientes))
      .catch(() => setErro("Falha ao carregar"));
  }

  useEffect(carregar, []);

  const cliente = clientes?.find((c) => c.id === id);

  async function excluirResponsavel(responsavelId: string) {
    await api.excluirResponsavel(responsavelId);
    carregar();
  }

  if (clientes === null) {
    return (
      <Layout>
        <main className="container">
          <p>Carregando…</p>
        </main>
      </Layout>
    );
  }

  if (!cliente) {
    return (
      <Layout>
        <main className="container">
          <div className="alerta alerta-erro" role="alert">
            Cliente não encontrado
          </div>
          <Link to="/clientes">← Voltar pra Clientes</Link>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="container predio-detalhe">
        <Link to="/clientes" className="predio-voltar">← Todos os prédios</Link>
        <header className="predio-cabecalho">
          <div className="predio-identidade">
            <div className="predio-icone" aria-hidden="true">⌂</div>
            <div>
              <span>PRÉDIO</span>
              <h1>{formatarNome(cliente.nome)}</h1>
              {cliente.administradora && <p>Administrado por {cliente.administradora}</p>}
            </div>
          </div>
          <dl className="predio-dados">
            {cliente.endereco && <div><dt>Endereço</dt><dd>{cliente.endereco}</dd></div>}
            {cliente.cidade && <div><dt>Localidade</dt><dd>{cliente.cidade}{cliente.estado ? `/${cliente.estado}` : ""}</dd></div>}
            {cliente.cnpj && <div><dt>CNPJ</dt><dd>{formatarCnpj(cliente.cnpj)}</dd></div>}
            {cliente.telefoneContato && <div><dt>Telefone</dt><dd>{cliente.telefoneContato}</dd></div>}
            {cliente.emailContato && <div><dt>E-mail de contato</dt><dd>{cliente.emailContato}</dd></div>}
          </dl>
        </header>

        {erro && (
          <div className="alerta alerta-erro" role="alert">
            {erro}
          </div>
        )}

        <div className="controle-segmentado" style={{ marginBottom: 16 }} aria-label="Seção do prédio">
          <button className={aba === "responsaveis" ? "ativo" : ""} onClick={() => setAba("responsaveis")}>Responsáveis</button>
          <button className={aba === "vistorias" ? "ativo" : ""} onClick={() => setAba("vistorias")}>Histórico de vistorias</button>
        </div>

        {aba === "vistorias" ? (
          <HistoricoVistorias clienteId={cliente.id} />
        ) : (
        <section className="responsaveis-secao">
          <header className="responsaveis-cabecalho">
            <div><span>RELATÓRIOS DE VISTORIA</span><h2>Responsáveis</h2><p>Defina quem recebe os relatórios deste prédio.</p></div>
            {admin && !mostrarForm && <button className="botao botao-primario" onClick={() => setMostrarForm(true)}>+ Adicionar responsável</button>}
          </header>

        {admin && (mostrarForm ? (
          <div className="cartao responsavel-formulario">
            <FormularioResponsavel clienteId={cliente.id} aoFechar={() => setMostrarForm(false)} aoCriar={carregar} />
          </div>
        ) : null)}

        {cliente.responsaveis.length === 0 ? (
          <div className="responsaveis-vazio">
            <div aria-hidden="true">✉</div>
            <div><strong>Nenhum responsável cadastrado</strong><p>{admin ? "Adicione o síndico, zelador ou gestor que deve receber os relatórios." : "Peça a um administrador para definir quem recebe os relatórios."}</p></div>
            {admin && <button className="botao botao-texto" onClick={() => setMostrarForm(true)}>Adicionar agora</button>}
          </div>
        ) : (
          <div className="cartao responsaveis-lista">
            {cliente.responsaveis.map((r) => (
              <div key={r.id} className="responsavel-linha">
                <div className="responsavel-avatar" aria-hidden="true">{r.nome.slice(0, 1).toUpperCase()}</div>
                <div className="responsavel-conteudo">
                  <span>{r.papel || "Responsável"}</span>
                  <strong>{r.nome}</strong>
                  <small>
                    {[r.email, r.telefone].filter(Boolean).join(" · ") || "Sem contato cadastrado"}
                    {!r.recebeRelatorio && <em> · não recebe relatório</em>}
                  </small>
                </div>
                {admin && <button className="botao botao-texto" style={{ marginRight: 16 }} onClick={() => void excluirResponsavel(r.id)}>Excluir</button>}
              </div>
            ))}
          </div>
        )}
        </section>
        )}
      </main>
    </Layout>
  );
}

/** Histórico completo de vistorias deste prédio, mais recente primeiro — inclui em andamento e
 * realizadas, cada uma com seu status, pra dar visão completa sem precisar ir pra Vistorias e
 * filtrar. Reaproveita listarVistorias() (já traz cliente.id) e filtra no client, mesmo padrão da
 * aba "Vistorias no período" em Clientes.tsx — evita rota nova só pra isso. */
function HistoricoVistorias({ clienteId }: { clienteId: string }) {
  const [vistorias, setVistorias] = useState<VistoriaResumoApi[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.listarVistorias().then(({ vistorias }) => setVistorias(vistorias)).catch(() => setErro("Falha ao carregar histórico de vistorias"));
  }, []);

  const doPredio = (vistorias ?? [])
    .filter((v) => v.cliente.id === clienteId)
    .sort((a, b) => b.dataVistoria.localeCompare(a.dataVistoria));

  return (
    <section>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}
      {vistorias === null ? (
        <p style={{ marginTop: 16 }}>Carregando…</p>
      ) : doPredio.length === 0 ? (
        <p style={{ marginTop: 16, color: "var(--texto-suave)" }}>Nenhuma vistoria registrada para este prédio ainda.</p>
      ) : (
        <div className="cartao" style={{ padding: 0, marginTop: 16 }}>
          {doPredio.map((v) => (
            <Link key={v.id} to={`/vistorias/${v.id}`} className="item-lista">
              <span className="item-lista-eyebrow">🏢 Vistoria</span>
              <div className="item-lista-titulo">{v.atribuidoA.nome}</div>
              <div className="item-lista-meta">
                <span>{new Date(v.dataVistoria).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</span>
                <PillStatusVistoria status={v.status} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function formatarNome(nome: string) {
  return nome.toLocaleLowerCase("pt-BR").replace(/(^|\s)\S/g, (letra) => letra.toLocaleUpperCase("pt-BR"));
}

function formatarCnpj(cnpj: string) {
  const numeros = cnpj.replace(/\D/g, "");
  return numeros.length === 14 ? numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : cnpj;
}

function FormularioResponsavel({
  clienteId,
  aoFechar,
  aoCriar,
}: {
  clienteId: string;
  aoFechar: () => void;
  aoCriar: () => void;
}) {
  const [nome, setNome] = useState("");
  const [papel, setPapel] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [recebeRelatorio, setRecebeRelatorio] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await api.criarResponsavel(clienteId, { nome, papel, email, telefone, recebeRelatorio });
      aoCriar();
      aoFechar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao criar responsável");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={aoEnviar}>
      <h3 style={{ marginTop: 0 }}>Novo responsável</h3>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor="r-nome">Nome</label>
      <input id="r-nome" value={nome} onChange={(e) => setNome(e.target.value)} required autoFocus />

      <label htmlFor="r-papel">Papel (síndico, zelador…)</label>
      <input id="r-papel" value={papel} onChange={(e) => setPapel(e.target.value)} />

      <label htmlFor="r-email">E-mail</label>
      <input id="r-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />

      <label htmlFor="r-telefone">Telefone</label>
      <input id="r-telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} />

      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
        <input
          type="checkbox"
          style={{ width: "auto" }}
          checked={recebeRelatorio}
          onChange={(e) => setRecebeRelatorio(e.target.checked)}
        />
        Recebe relatório
      </label>

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
